import { DatePickerFr } from '../ui/DatePickerFr';
import React, { useState } from 'react';
import { Search, X, MapPin, Calendar, DollarSign, User } from 'lucide-react';
import { toast } from 'sonner';

const AdvancedSearch = ({ onSearch, onClose }) => {
  const [filters, setFilters] = useState({
    type: 'all', // chantier, dommage, travaux, membre, cooper, all
    territoire: '',
    codePostal: '',
    ville: '',
    dateDebut: '',
    dateFin: '',
    montantMin: '',
    montantMax: '',
    statut: '',
    expertId: '',
    entrepriseId: '',
    maitreouvrage: ''
  });

  const handleChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    // Valider au moins un critère
    const hasFilters = Object.values(filters).some(val => val !== '' && val !== 'all');
    
    if (!hasFilters) {
      toast.warning('Veuillez sélectionner au moins un critère de recherche');
      return;
    }

    // Filtrer les valeurs vides
    const activeFilters = Object.entries(filters).reduce((acc, [key, value]) => {
      if (value !== '' && value !== 'all') {
        acc[key] = value;
      }
      return acc;
    }, {});

    onSearch(activeFilters);
    toast.success(`Recherche lancée avec ${Object.keys(activeFilters).length} critère(s)`);
  };

  const handleReset = () => {
    setFilters({
      type: 'all',
      territoire: '',
      codePostal: '',
      ville: '',
      dateDebut: '',
      dateFin: '',
      montantMin: '',
      montantMax: '',
      statut: '',
      expertId: '',
      entrepriseId: '',
      maitreouvrage: ''
    });
    onSearch(null);
    toast.info('Filtres réinitialisés');
  };

  return (
    <div className="advanced-search-modal">
      <div className="search-modal-overlay" onClick={onClose}></div>
      
      <div className="search-modal-content">
        <div className="search-modal-header">
          <div className="header-left">
            <Search className="h-6 w-6" />
            <h2>Recherche Multi-Critères</h2>
          </div>
          <button onClick={onClose} className="close-modal-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="search-modal-body">
          {/* Type de données */}
          <div className="search-section">
            <h3>🎯 Type de Données</h3>
            <select
              value={filters.type}
              onChange={(e) => handleChange('type', e.target.value)}
              className="search-input"
            >
              <option value="all">Tous les types</option>
              <option value="chantier">Chantiers RCDS</option>
              <option value="dommage">Dommages</option>
              <option value="travaux">Travaux Réparation</option>
              <option value="membre">Membres</option>
              <option value="cooper">COOPER's</option>
            </select>
          </div>

          {/* Localisation */}
          <div className="search-section">
            <h3><MapPin className="inline h-5 w-5" /> Localisation</h3>
            <div className="search-grid-3">
              <input
                type="text"
                placeholder="Territoire / Région"
                value={filters.territoire}
                onChange={(e) => handleChange('territoire', e.target.value)}
                className="search-input"
              />
              <input
                type="text"
                placeholder="Code Postal"
                value={filters.codePostal}
                onChange={(e) => handleChange('codePostal', e.target.value)}
                className="search-input"
              />
              <input
                type="text"
                placeholder="Ville"
                value={filters.ville}
                onChange={(e) => handleChange('ville', e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {/* Période */}
          <div className="search-section">
            <h3><Calendar className="inline h-5 w-5" /> Période</h3>
            <div className="search-grid-2">
              <div>
                <label className="search-label">Date de début</label>
                <DatePickerFr
                  value={filters.dateDebut}
                  onChange={(v) => handleChange('dateDebut', v)}
                  className="search-input w-full"
                />
              </div>
              <div>
                <label className="search-label">Date de fin</label>
                <DatePickerFr
                  value={filters.dateFin}
                  onChange={(v) => handleChange('dateFin', v)}
                  className="search-input w-full"
                />
              </div>
            </div>
          </div>

          {/* Montants */}
          <div className="search-section">
            <h3><DollarSign className="inline h-5 w-5" /> Montants (€)</h3>
            <div className="search-grid-2">
              <input
                type="number"
                placeholder="Montant minimum"
                value={filters.montantMin}
                onChange={(e) => handleChange('montantMin', e.target.value)}
                className="search-input"
              />
              <input
                type="number"
                placeholder="Montant maximum"
                value={filters.montantMax}
                onChange={(e) => handleChange('montantMax', e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {/* Statut */}
          <div className="search-section">
            <h3>📊 Statut</h3>
            <select
              value={filters.statut}
              onChange={(e) => handleChange('statut', e.target.value)}
              className="search-input"
            >
              <option value="">Tous les statuts</option>
              <option value="en_cours">En cours</option>
              <option value="en_attente">En attente</option>
              <option value="terminé">Terminé</option>
              <option value="validé">Validé</option>
              <option value="suspendu">Suspendu</option>
            </select>
          </div>

          {/* Intervenants */}
          <div className="search-section">
            <h3><User className="inline h-5 w-5" /> Intervenants</h3>
            <div className="search-grid-3">
              <input
                type="text"
                placeholder="ID Expert"
                value={filters.expertId}
                onChange={(e) => handleChange('expertId', e.target.value)}
                className="search-input"
              />
              <input
                type="text"
                placeholder="ID Entreprise"
                value={filters.entrepriseId}
                onChange={(e) => handleChange('entrepriseId', e.target.value)}
                className="search-input"
              />
              <input
                type="text"
                placeholder="Maître d'ouvrage"
                value={filters.maitreouvrage}
                onChange={(e) => handleChange('maitreouvrage', e.target.value)}
                className="search-input"
              />
            </div>
          </div>
        </div>

        <div className="search-modal-footer">
          <button onClick={handleReset} className="search-btn secondary">
            Réinitialiser
          </button>
          <button onClick={handleSearch} className="search-btn primary">
            <Search className="h-5 w-5" />
            Rechercher
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSearch;
